/**
 * NewGameScene — the gentle new-game flow. Title -> *NewGame* -> Town.
 *
 * Three calm steps, no pressure, no wrong answers:
 *   1. Pick one of the three Kanto starters, shown with its real battle sprite
 *      (resolved through the shared SpriteResolver's full fallback chain). The chosen
 *      starter walks behind you as a follower once you set out.
 *   2. Name the trainer via the touch-first TextInput (OS keyboard on phones).
 *   3. Choose your trainer from a small gallery of REAL Pokemon-game trainers — the
 *      actual Showdown sprites the Foundation vendored (makePlayerTrainer +
 *      PLAYER_TRAINER_CHOICES). Tap a face to pick it.
 *      (Residents are also real trainers; the chosen one becomes the player.)
 * Then GameSession.createNew mints the world (the starter becomes the follower), the
 * session is stashed on the registry, and we fade into Town. Per D16 (multi-slot
 * saves) createNew now ALWAYS mints a fresh slot, so New Game never overwrites an
 * existing save — there is no overwrite/confirm path to handle here.
 *
 * Responsive + touch-first throughout (Layout + the UI kit). The hard cross-platform
 * rule is that EVERY control must be reachable on a tall, narrow PHONE in portrait
 * (~390x844). To guarantee that:
 *   - The heading is pinned to the top and the "Begin" button is pinned to a fixed
 *     bottom bar that is ALWAYS visible — so you can always start a game.
 *   - Everything in between (starter cards, name field, the whole trainer gallery)
 *     lives in a vertically SCROLLABLE viewport (touch-drag + wheel via ScrollList),
 *     so when the content is taller than the phone it simply scrolls — no control
 *     ever falls below the fold and out of reach.
 *   - On a narrow/portrait viewport the layout COMPACTS: the 3 starters lay out in a
 *     horizontal row (smaller cards) instead of a tall stack, the trainer cards pack
 *     into a tight grid sized to the width, and paddings/sizes shrink — so far less
 *     scrolling is needed in the first place.
 *   - On a roomy desktop the original height-aware centered stack is preserved.
 */
import Phaser from 'phaser';
import { GameSession } from '@/session';
import type { SpeciesData } from '@/shared';
import type { DataIndex } from '@/data';
import { FIXTURE_SPECIES } from '@/data';
import { COLORS, CSS, FONT, RADIUS, SPACING } from './ui/theme.js';
import { Panel } from './ui/Panel.js';
import { Button } from './ui/Button.js';
import { TextInput } from './ui/TextInput.js';
import { ScrollList } from './ui/ScrollList.js';
import { Layout } from './layout/index.js';
import { SpriteResolver } from './sprites/index.js';
import {
  makePlayerTrainer,
  PLAYER_TRAINER_CHOICES,
  DEFAULT_TRAINER_KEY,
  type TrainerAvatar,
  type TrainerChoice,
} from './sprites/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { pick, isSelected } from './pickerNav.js';
import { REGISTRY_DATA_INDEX, REGISTRY_SPRITES } from './PreloadScene.js';
import { setSession, sessionDeps } from './session.js';

/** The three Kanto starters, in their classic order. */
const STARTER_IDS = ['bulbasaur', 'charmander', 'squirtle'] as const;

/** Roomy starter-card footprint (desktop / portrait-but-roomy). */
const STARTER_CARD_W = 132;
const STARTER_CARD_H = 158;
/**
 * Compact starter-card footprint — three across a narrow phone in a tidy row.
 * Kept tall enough that the fitted sprite + the name + the type label all stack with
 * clear padding and nothing overlaps on a ~390px phone.
 */
const STARTER_CARD_W_COMPACT = 108;
const STARTER_CARD_H_COMPACT = 146;

/**
 * Vertical layout bands inside a starter card (measured from the card's local center,
 * +y downward). The sprite gets the upper art band; the name + type labels sit in a
 * fixed lower band so the name ALWAYS clears the sprite with padding. Used by both the
 * sprite fit and the label placement so they can never collide.
 */
const STARTER_NAME_GAP = 30; // name baseline above the card bottom
const STARTER_TYPE_GAP = 12; // type subtitle above the card bottom
const STARTER_ART_PAD = 10; // gap between the sprite's bottom and the name's top
const STARTER_ART_TOP_PAD = 10; // gap from the card top to the sprite's top

/** Trainer gallery card footprint (a tidy tappable tile per face). */
const TRAINER_CARD_W = 92;
const TRAINER_CARD_H = 108;
/** Compact trainer-card footprint — tighter tiles so they pack on a phone width. */
const TRAINER_CARD_W_COMPACT = 78;
const TRAINER_CARD_H_COMPACT = 94;

interface StarterCard {
  readonly species: SpeciesData;
  readonly container: Phaser.GameObjects.Container;
  readonly panel: Panel;
  readonly sprite: Phaser.GameObjects.Image;
  /** Accent outline shown only when this card is the chosen one. */
  ring: Phaser.GameObjects.Graphics;
  readonly name: Phaser.GameObjects.Text;
  readonly typeLabel: Phaser.GameObjects.Text;
  /** Current card footprint, so a sprite that loads after a relayout fits correctly. */
  w: number;
  h: number;
}

/** One trainer card: the live Showdown trainer sprite + a soft panel that lifts when chosen. */
interface TrainerCard {
  readonly choice: TrainerChoice;
  readonly container: Phaser.GameObjects.Container;
  readonly panel: Panel;
  /** The live trainer avatar handle (the actual ripped sprite). */
  readonly avatar: TrainerAvatar;
  /** Accent outline shown only when this card is the chosen one. */
  ring: Phaser.GameObjects.Graphics;
  readonly name: Phaser.GameObjects.Text;
}

/** Resolved, viewport-aware sizing for the whole picker. */
interface SizeProfile {
  readonly starterW: number;
  readonly starterH: number;
  readonly starterGap: number;
  readonly trainerW: number;
  readonly trainerH: number;
  readonly trainerGap: number;
  /** Gap between major sections. */
  readonly sec: number;
  /** Gap between a label and its control. */
  readonly lbl: number;
}

/**
 * Draw an accent selection ring (rounded outline) sized to a card, hidden by default.
 * The ring is INSET a few px from the card edge so a bottom-anchored label (e.g. the
 * starter card's "grass / poison" type subtitle) never gets crossed/clipped by the line.
 */
function makeSelectionRing(
  scene: Phaser.Scene,
  width: number,
  height: number,
  inset = 4,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  g.lineStyle(3, COLORS.accent, 1);
  g.strokeRoundedRect(
    -width / 2 + inset,
    -height / 2 + inset,
    width - inset * 2,
    height - inset * 2,
    RADIUS.md,
  );
  g.setVisible(false);
  return g;
}

export class NewGameScene extends Phaser.Scene {
  private layout!: Layout;
  private index!: DataIndex;
  private resolver!: SpriteResolver;
  private reduced = false;

  private heading?: Phaser.GameObjects.Text;

  /** The scrolling viewport that holds every control between the heading + Begin bar. */
  private list?: ScrollList;
  /** The fixed bottom bar backing the always-visible Begin button. */
  private beginBar?: Phaser.GameObjects.Graphics;

  private starterCards: StarterCard[] = [];
  private chosenStarterId: string = STARTER_IDS[0];

  private nameInput?: TextInput;
  private nameLabel?: Phaser.GameObjects.Text;

  private trainerLabel?: Phaser.GameObjects.Text;
  private trainerCards: TrainerCard[] = [];
  private chosenTrainerKey: string = PLAYER_TRAINER_CHOICES[0]?.key ?? DEFAULT_TRAINER_KEY;

  private beginButton?: Button;
  private busy = false;

  constructor() {
    super('NewGame');
  }

  create(): void {
    // Phaser REUSES the scene instance, so a re-entry runs create() again over the
    // prior visit's objects/handlers/arrays. If the card arrays aren't reset and the
    // old cards aren't destroyed, the new cards stack on top of dead ones whose
    // POINTER_DOWN handlers still fire — so taps land on a stale card and selection
    // appears "stuck". Tear the prior visit down first.
    this.teardown();
    this.busy = false;
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.index =
      (this.registry.get(REGISTRY_DATA_INDEX) as DataIndex | undefined) ?? fixtureIndex();
    this.resolver =
      (this.registry.get(REGISTRY_SPRITES) as SpriteResolver | undefined) ??
      new SpriteResolver(this);
    this.reduced = isReducedMotion();

    this.heading = this.add
      .text(0, 0, 'Who will you set out with?', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // The bottom bar + Begin button live OUTSIDE the scroll viewport so they stay
    // pinned and always reachable on a tall, narrow phone (the core of the fix).
    this.beginBar = this.add.graphics();
    this.beginButton = new Button(this, 0, 0, {
      label: 'Begin',
      variant: 'primary',
      width: 240,
      reducedMotion: this.reduced,
      onClick: () => void this.begin(),
    });

    // Everything else (starters, name, trainer gallery) goes in the scrolling list so
    // it can never overflow off the bottom of a phone unreachably.
    this.list = new ScrollList(this, 0, 0, { width: 100, height: 100 });
    this.buildStarterCards();
    this.buildNameField();
    this.buildTrainerPicker();

    this.selectStarter(this.chosenStarterId);
    this.selectTrainer(this.chosenTrainerKey);
    this.relayout();
    this.layout.on('change', this.relayout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.teardown, this);
    fadeIn(this);
  }

  /**
   * Tear down every object/handler/array from a prior visit so a re-entry starts
   * clean (Phaser reuses the scene instance). Idempotent: safe before anything is
   * built, on the very first create().
   */
  private teardown(): void {
    this.layout?.off('change', this.relayout, this);

    // Cards/name/trainer-label live inside the ScrollList, which cascades destroy to
    // its content; still null the arrays so nothing stale is touched on re-entry.
    this.list?.destroy();
    this.list = undefined;
    this.starterCards = [];
    this.trainerCards = [];

    this.heading?.destroy();
    this.heading = undefined;
    this.nameLabel?.destroy();
    this.nameLabel = undefined;
    this.nameInput?.destroy();
    this.nameInput = undefined;
    this.trainerLabel?.destroy();
    this.trainerLabel = undefined;
    this.beginBar?.destroy();
    this.beginBar = undefined;
    this.beginButton?.destroy();
    this.beginButton = undefined;

    // Reset the chosen ids back to their defaults so a fresh New Game starts neutral.
    this.chosenStarterId = STARTER_IDS[0];
    this.chosenTrainerKey = PLAYER_TRAINER_CHOICES[0]?.key ?? DEFAULT_TRAINER_KEY;
  }

  // ── starter cards ────────────────────────────────────────────────────────────

  private buildStarterCards(): void {
    for (const id of STARTER_IDS) {
      const species = this.index.speciesById(id) ?? FIXTURE_SPECIES.find((s) => s.id === id)!;
      const container = this.add.container(0, 0);
      const panel = new Panel(this, 0, 0, {
        width: STARTER_CARD_W,
        height: STARTER_CARD_H,
        fill: COLORS.surface,
      });
      const sprite = this.add.image(0, -14, '__MISSING').setVisible(false);
      const name = this.add
        .text(0, 48, species.name, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.text,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      // Sit the type subtitle a touch higher so the inset selection ring's bottom edge
      // clears the text instead of crossing "grass / poison".
      const typeLabel = this.add
        .text(0, 64, species.types.join(' / '), {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textMuted,
        })
        .setOrigin(0.5);

      const ring = makeSelectionRing(this, STARTER_CARD_W, STARTER_CARD_H);
      container.add([panel, sprite, name, typeLabel, ring]);
      container.setSize(STARTER_CARD_W, STARTER_CARD_H);
      container.setInteractive(
        new Phaser.Geom.Rectangle(
          -STARTER_CARD_W / 2,
          -STARTER_CARD_H / 2,
          STARTER_CARD_W,
          STARTER_CARD_H,
        ),
        Phaser.Geom.Rectangle.Contains,
      );
      container.input!.cursor = 'pointer';
      // Pick on TAP (POINTER_UP); a drag-scroll of the card list no-ops here.
      container.on(Phaser.Input.Events.POINTER_UP, () => {
        if (!this.list?.wasDragging()) this.selectStarter(id);
      });

      this.list?.content.add(container);
      const card: StarterCard = {
        species,
        container,
        panel,
        sprite,
        ring,
        name,
        typeLabel,
        w: STARTER_CARD_W,
        h: STARTER_CARD_H,
      };
      this.starterCards.push(card);
      void this.loadStarterSprite(card);
    }
  }

  /** Resolve + show the species' real battle sprite (front), fallback chain inside. */
  private async loadStarterSprite(card: StarterCard): Promise<void> {
    const resolved = await this.resolver.battle(card.species.dexNo, 'front');
    // The resolve is ASYNC: by the time it lands the scene may have been torn down /
    // re-entered (Phaser reuses the instance), the sprite may be destroyed, or the
    // texture may not have finished loading into the manager — any of which makes
    // setTexture throw ("Cannot read properties of undefined (reading 'sys')"). Confirm
    // the scene is still active, the sprite still lives, and the texture exists before
    // touching it; skip safely otherwise.
    if (
      !resolved ||
      !this.scene.isActive() ||
      !card.sprite.active ||
      !card.sprite.scene ||
      !this.textures.exists(resolved.key)
    ) {
      return;
    }
    card.sprite.setTexture(resolved.key);
    // Fit to the CURRENT card footprint (a relayout may have shrunk it before the sprite
    // resolved), so the art always sits in its band above the name and never overlaps it.
    this.fitStarterSprite(card);
    card.sprite.setVisible(true);
  }

  /**
   * Scale + seat the starter sprite inside the card's UPPER art band — the region from
   * the card top (minus a little headroom) down to the name label (minus padding). The
   * sprite is fitted to that band's height/width so its bottom always clears the name,
   * which is the core of the no-overlap fix on a short phone card.
   */
  private fitStarterSprite(card: StarterCard): void {
    const { w, h } = card;
    const artTop = -h / 2 + STARTER_ART_TOP_PAD;
    // The band ends a padding above the name label's TOP edge (name center minus its
    // half-height), so the fitted sprite's bottom always clears the name text itself.
    const nameTop = h / 2 - STARTER_NAME_GAP - (card.name.height || 16) / 2;
    const artBottom = nameTop - STARTER_ART_PAD;
    const bandH = Math.max(24, artBottom - artTop);
    const bandW = Math.max(24, w - SPACING.md);
    const sw = card.sprite.width || 1;
    const sh = card.sprite.height || 1;
    const scale = Math.min(bandW / sw, bandH / sh, 3);
    card.sprite.setScale(scale);
    // Center the sprite within the art band so equal headroom/footroom frames it.
    card.sprite.setPosition(0, (artTop + artBottom) / 2);
  }

  private selectStarter(id: string): void {
    const options = this.starterCards.map((c) => c.species.id);
    this.chosenStarterId = pick(options, this.chosenStarterId, id);
    for (const card of this.starterCards) {
      const selected = isSelected(card.species.id, this.chosenStarterId);
      // Gentle, calm selection cue: the chosen card lifts + brightens, with a clear
      // accent ring so the active choice is unmistakable (and re-selectable).
      card.container.setScale(selected ? 1.04 : 1);
      card.container.setAlpha(selected ? 1 : 0.78);
      card.ring.setVisible(selected);
    }
  }

  // ── name field ──────────────────────────────────────────────────────────────

  private buildNameField(): void {
    this.nameLabel = this.add
      .text(0, 0, 'Your name', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    this.nameInput = new TextInput(this, 0, 0, {
      width: 280,
      placeholder: 'trainer name',
      maxLength: 16,
      onSubmit: () => void this.begin(),
    });
    this.list?.content.add(this.nameLabel);
    this.list?.content.add(this.nameInput);
  }

  // ── trainer (real Showdown trainer) gallery ────────────────────────────────────

  private buildTrainerPicker(): void {
    this.trainerLabel = this.add
      .text(0, 0, 'Choose your trainer', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);
    this.list?.content.add(this.trainerLabel);

    for (const choice of PLAYER_TRAINER_CHOICES) {
      const container = this.add.container(0, 0);
      const panel = new Panel(this, 0, 0, {
        width: TRAINER_CARD_W,
        height: TRAINER_CARD_H,
        fill: COLORS.surface,
      });
      // The real ripped Showdown trainer — origin is feet-centre, so seat it in the
      // panel's art area with a little headroom and a label below.
      const avatar = makePlayerTrainer(this, choice.key, 0, TRAINER_CARD_H / 2 - 28);
      avatar.sprite.setScale(0.7);
      const name = this.add
        .text(0, TRAINER_CARD_H / 2 - 16, choice.label, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.text,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      const ring = makeSelectionRing(this, TRAINER_CARD_W, TRAINER_CARD_H);
      container.add([panel, avatar.sprite, name, ring]);
      container.setSize(TRAINER_CARD_W, TRAINER_CARD_H);
      container.setInteractive(
        new Phaser.Geom.Rectangle(
          -TRAINER_CARD_W / 2,
          -TRAINER_CARD_H / 2,
          TRAINER_CARD_W,
          TRAINER_CARD_H,
        ),
        Phaser.Geom.Rectangle.Contains,
      );
      container.input!.cursor = 'pointer';
      // Pick on TAP (POINTER_UP); a drag-scroll of the card list no-ops here.
      container.on(Phaser.Input.Events.POINTER_UP, () => {
        if (!this.list?.wasDragging()) this.selectTrainer(choice.key);
      });

      this.list?.content.add(container);
      this.trainerCards.push({ choice, container, panel, avatar, ring, name });
    }
  }

  private selectTrainer(trainerKey: string): void {
    const options = this.trainerCards.map((c) => c.choice.key);
    this.chosenTrainerKey = pick(options, this.chosenTrainerKey, trainerKey);
    for (const card of this.trainerCards) {
      const selected = isSelected(card.choice.key, this.chosenTrainerKey);
      card.container.setScale(selected ? 1.06 : 1);
      card.container.setAlpha(selected ? 1 : 0.7);
      card.ring.setVisible(selected);
    }
  }

  // ── commit ────────────────────────────────────────────────────────────────────

  private async begin(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.beginButton?.setDisabled(true);

    const rawName = this.nameInput?.getValue().trim() ?? '';
    const trainerName = rawName.length > 0 ? rawName : 'Wanderer';

    try {
      const session = await GameSession.createNew(
        {
          trainerName,
          starterSpeciesId: this.chosenStarterId,
          trainerKey: this.chosenTrainerKey,
        },
        sessionDeps(),
      );
      setSession(this, session);
      goTo(this, 'Town');
    } catch {
      // createNew only throws when the dataset has no species at all — which the
      // fixtures fallback prevents. Re-enable so the player can retry rather than stall.
      this.busy = false;
      this.beginButton?.setDisabled(false);
    }
  }

  // ── layout ────────────────────────────────────────────────────────────────────

  /**
   * Pick the sizing profile for the current viewport. A narrow/portrait phone (or a
   * short viewport) COMPACTS everything — smaller cards, tighter gaps — so far less
   * content overflows; a roomy desktop keeps the original generous sizes.
   */
  private sizeProfile(): SizeProfile {
    const safe = this.layout.safe;
    const compact = this.layout.isCompact || safe.height < 720;
    if (compact) {
      return {
        starterW: STARTER_CARD_W_COMPACT,
        starterH: STARTER_CARD_H_COMPACT,
        starterGap: SPACING.sm,
        trainerW: TRAINER_CARD_W_COMPACT,
        trainerH: TRAINER_CARD_H_COMPACT,
        trainerGap: SPACING.sm,
        sec: SPACING.md,
        lbl: SPACING.xs,
      };
    }
    return {
      starterW: STARTER_CARD_W,
      starterH: STARTER_CARD_H,
      starterGap: SPACING.md,
      trainerW: TRAINER_CARD_W,
      trainerH: TRAINER_CARD_H,
      trainerGap: SPACING.md,
      sec: SPACING.lg,
      lbl: SPACING.sm,
    };
  }

  /** Rescale a starter card (panel, ring, hit area, sprite fit) to a footprint. */
  private resizeStarterCard(card: StarterCard, w: number, h: number): void {
    card.w = w;
    card.h = h;
    card.panel.resize(w, h);
    card.ring.destroy();
    card.ring = makeSelectionRing(this, w, h);
    card.container.add(card.ring);
    card.ring.setVisible(isSelected(card.species.id, this.chosenStarterId));
    card.container.setSize(w, h);
    card.container.input!.hitArea = new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h);
    // Anchor the name + type labels in the card's fixed LOWER band, then fit the sprite
    // into the art band ABOVE them — so the sprite's bottom always clears the name with
    // padding (the no-overlap guarantee, even on the short compact card).
    card.name.setPosition(0, h / 2 - STARTER_NAME_GAP);
    card.typeLabel.setPosition(0, h / 2 - STARTER_TYPE_GAP);
    this.fitStarterSprite(card);
  }

  /** Rescale a trainer card (panel, ring, hit area, avatar seat) to a footprint. */
  private resizeTrainerCard(card: TrainerCard, w: number, h: number): void {
    card.panel.resize(w, h);
    card.ring.destroy();
    card.ring = makeSelectionRing(this, w, h);
    card.container.add(card.ring);
    card.ring.setVisible(isSelected(card.choice.key, this.chosenTrainerKey));
    card.container.setSize(w, h);
    card.container.input!.hitArea = new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h);
    card.avatar.sprite.setPosition(0, h / 2 - 28);
    card.name.setPosition(0, h / 2 - 14);
  }

  private relayout(): void {
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const p = this.sizeProfile();

    // Apply the chosen footprints to every card before measuring the stack.
    for (const c of this.starterCards) this.resizeStarterCard(c, p.starterW, p.starterH);
    for (const c of this.trainerCards) this.resizeTrainerCard(c, p.trainerW, p.trainerH);

    // ── Pinned chrome: heading at the top, Begin in a fixed bottom bar ───────────
    const headingH = this.heading?.height ?? 26;
    const headingY = safe.y + SPACING.md + headingH / 2;
    this.heading?.setPosition(cx, headingY);

    const beginH = this.beginButton?.buttonHeight ?? 48;
    const beginW = this.beginButton?.buttonWidth ?? 240;
    const barH = beginH + SPACING.md * 2;
    const barTop = safe.y + safe.height - barH;
    const beginY = barTop + barH / 2;
    this.beginBar?.clear();
    this.beginBar
      ?.fillStyle(COLORS.bg, 1)
      .fillRect(safe.x, barTop, safe.width, barH)
      .lineStyle(1.5, COLORS.border, 1)
      .lineBetween(safe.x, barTop, safe.x + safe.width, barTop);
    // Fit the Begin button to a narrow phone width (still a big tap target).
    const fitBeginW = Math.min(beginW, safe.width - SPACING.lg);
    this.beginButton?.setWidth(fitBeginW);
    this.beginButton?.setPosition(cx, beginY);

    // ── The scrolling viewport sits between the heading and the Begin bar ────────
    const listTop = headingY + headingH / 2 + p.sec;
    const listH = Math.max(80, barTop - SPACING.sm - listTop);
    const listW = safe.width;
    // Position the list FIRST: ScrollList.resize() rebuilds its world-space clip mask
    // from the container's current (x, y), so the mask only aligns if we move it before
    // we resize. (Resizing then moving would leave the mask at the old position.)
    this.list?.setPosition(cx, listTop + listH / 2);
    this.list?.resize(listW, listH);

    // ── Lay the picker content out inside the list (local space, origin centered) ─
    // Local Y in the list runs from -listH/2 (top) downward; we track a cursor and
    // convert section centers from a top-anchored 0 origin to that local space.
    const measured = this.layoutContent(listW, p);
    // The list clips/scrolls to its own viewport; report the full content height so
    // touch-drag + wheel can reach the bottom (the trainer grid) on a phone.
    this.list?.setContentHeight(measured);
  }

  /**
   * Place the starter row, name field, trainer label, and trainer grid INSIDE the
   * scroll list's content container. Content is laid top-anchored from local y=0; the
   * ScrollList shifts the whole content up by the scroll offset, so a tall stack on a
   * phone scrolls into reach. Returns the total content height.
   *
   * All card containers are children of `list.content`, whose own origin sits at the
   * viewport center, so x=0 is the horizontal center and content y=0 (set by the
   * ScrollList to the viewport top) is the content TOP. We position relative to that
   * local frame: x is offset from center, y grows downward from the content top.
   */
  private layoutContent(listW: number, p: SizeProfile): number {
    const content = this.list?.content;
    if (!content) return 0;

    // The content container shares the list's center as its origin; ScrollList sets
    // content.y = -vpHeight/2 - offset so local y=0 is the content TOP. X is in the
    // list's local frame (0 = horizontal center), so center on 0, not on `cx`.

    // Starter row: three across. They fit a phone width at the compact size; if even
    // that is too wide, wrap is unnecessary (3 small cards always fit >= ~340px), so a
    // simple centered row is correct and keeps every starter tappable.
    const starterRowW = p.starterW * 3 + p.starterGap * 2;
    const starterFits = starterRowW <= listW - SPACING.sm;

    let y = 0;
    if (starterFits) {
      const startX = -starterRowW / 2 + p.starterW / 2;
      const rowCy = y + p.starterH / 2;
      this.starterCards.forEach((card, i) => {
        card.container.setPosition(startX + i * (p.starterW + p.starterGap), rowCy);
      });
      y += p.starterH;
    } else {
      // Extremely narrow fallback: stack the three starters vertically (still in the
      // scroller, so all reachable).
      this.starterCards.forEach((card, i) => {
        card.container.setPosition(0, y + p.starterH / 2 + i * (p.starterH + p.starterGap));
      });
      y += p.starterH * 3 + p.starterGap * 2;
    }
    y += p.sec;

    // Name label + input.
    const nameLabelH = this.nameLabel?.height ?? 18;
    this.nameLabel?.setPosition(0, y + nameLabelH / 2);
    y += nameLabelH + p.lbl;
    const inputH = this.nameInput?.height ?? 48;
    // Fit the input to the viewport width on a phone.
    this.nameInput?.setPosition(0, y + inputH / 2);
    y += inputH + p.sec;

    // Trainer label.
    const trainerLabelH = this.trainerLabel?.height ?? 18;
    this.trainerLabel?.setPosition(0, y + trainerLabelH / 2);
    y += trainerLabelH + p.lbl;

    // Trainer grid: as many columns as fit the list width, wrapping into rows. On a
    // ~390px phone this packs the eight cards into a tidy 4x2 (or 3x3) grid; each row
    // is centered on its own count so a short final row still looks intentional.
    const count = this.trainerCards.length;
    const cell = p.trainerW + p.trainerGap;
    const maxFit = Math.max(1, Math.floor((listW - SPACING.sm + p.trainerGap) / cell));
    const cols = Math.max(1, Math.min(count, maxFit));
    const rowWidthFor = (n: number): number => n * p.trainerW + (n - 1) * p.trainerGap;
    const gridTop = y;
    this.trainerCards.forEach((card, i) => {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const inThisRow = Math.min(cols, count - row * cols);
      const startX = -rowWidthFor(inThisRow) / 2 + p.trainerW / 2;
      const cyRow = gridTop + row * (p.trainerH + p.trainerGap) + p.trainerH / 2;
      card.container.setPosition(startX + col * cell, cyRow);
    });
    const rows = Math.max(1, Math.ceil(count / cols));
    y += rows * p.trainerH + (rows - 1) * p.trainerGap;
    y += p.sec; // a little breathing room at the bottom of the scroll

    return y;
  }
}

/** Build a fixtures-backed index when the registry has none (defensive). */
function fixtureIndex(): DataIndex {
  const byId = new Map(FIXTURE_SPECIES.map((s) => [s.id, s]));
  const byDex = new Map(FIXTURE_SPECIES.map((s) => [s.dexNo, s]));
  return {
    speciesById: (id) => byId.get(id),
    speciesByDexNo: (dexNo) => byDex.get(dexNo),
    moveById: () => undefined,
  };
}
